import { FORM_ERROR, SubmissionErrors } from 'final-form';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { fetchAttributesList } from '~src/modules/attributes/actions/attributes.actions';

import logger from '~src/services/logger/LlsLogger';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { getUsers } from '~src/modules/users/reducers/userManagementReducer';
import {
  changeUserActiveStatus,
  deleteUser,
  FetchUserParams,
  fetchUsers,
  updateUser,
} from '~src/modules/users/actions/user.actions';
import { UserFormValues, UsersRespI } from '~src/modules/users/interfaces';
import { MainContainerAnimation } from '~src/styles/animations';
import {
  getUserFullName,
  partialUserCoreToPatch,
  User,
  UserCore,
  UserCorePatch,
} from '~src/interfaces/user.interface';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { StoreI } from '~src/rootReducer';
import { FilterAndSortingConf } from '~src/modules/users/containers/UserManagement';

import { ItemsAmountRow, NoUserFound } from './usersList.sc';
import UserListItem from './UserListItem';
import {objectDiff} from "~src/utils/objectDiff";
import {TFunction} from "i18next";

interface OwnProps extends Pick<RouteComponentProps<any>, 'history' | 'location'> {
  t: TFunction;
  updateAttributes: (
    userId: string,
    values: UserFormValues,
    initialValues: Partial<UserFormValues>,
  ) => Promise<void>;
  getFetchUserParams: (forRest?: boolean) => FilterAndSortingConf;
  openItem?: string;
  changeOpenItem?: (userId?: string) => void;
}

interface StateProps {
  users: User[] | null;
  loadingUsers: boolean;
}

interface DispatchProps {
  fetchUsers: (params: FetchUserParams) => ApiReduxResp<UsersRespI>;
  deleteUser: (user: User) => ApiReduxResp;
  updateUser: (userId: string, values: UserCorePatch, fullName: string) => ApiReduxResp<User>;
  changeUserActiveStatus: (userId: string, deactivated: boolean, fullName: string) => ApiReduxResp<User>;
  fetchAttributesList: () => ApiReduxResp<unknown>
}

type Props = OwnProps & StateProps & DispatchProps;

interface State {
  openItem: string | void;
}

class UserList extends React.Component<Props, State> {
  static defaultProps = {
    users: null,
    loadingUsers: false,
  };

  state: State = { openItem: undefined };

  changeOpenItem = (userId?: string) => {
    this.setState({ openItem: userId });
    if (this.props.changeOpenItem) {
      this.props.changeOpenItem(userId);
    }
  };

  onDeleteUser = (user: User) => {
    return this.props
      .deleteUser(user)
      .then(({ response }) => {
        if (response) {
          this.props.fetchUsers(this.props.getFetchUserParams(false));
        } else {
          throw new Error(this.props.t('x.users.delete_error'));
        }
      })
      .catch(error => {
        logger.info('Error deleting user', {
          error,
          info: {
            componentStack: 'UsersList.tsx',
          },
        });
      });
  };

  onToggleUserDeactivatedStatus = (user: User) => {
    return this.props
      .changeUserActiveStatus(user.uuid, !user.deactivated, `${user.firstname} ${user.lastname}`)
      .then(({ response }) => {
        if (response) {
          this.changeOpenItem();
          this.props.fetchUsers(this.props.getFetchUserParams(false));

          // need to update local attribute cache
          // as deactivation removes user from partial visibilities:
          void this.props.fetchAttributesList();

        } else {
          throw new Error(this.props.t('x.users.activation.error'));
        }
      })
      .catch(error => {
        logger.info('Error toggling user deactivated status', {
          error,
          info: {
            componentStack: 'UsersList.tsx',
          },
        });
      });
  };

  updateFullUser = (userId: string, values: UserFormValues, initialValues: Partial<UserFormValues>) => {
    const { t } = this.props;
    const coreValues = partialUserCoreToPatch(values);
    const coreInitialValues = partialUserCoreToPatch(initialValues);
    const changedCoreValues: Partial<UserCore> = objectDiff(coreInitialValues, coreValues);

    const fullName = getUserFullName(values);
    return new Promise<void>(async res => {
      Promise.all([
        this.props.updateUser(userId, changedCoreValues, fullName),
        this.props.updateAttributes(userId, values, initialValues),
      ])
        .then(response => {
          if (response[0] && response[0].error) {
            const errors: SubmissionErrors = { [FORM_ERROR]: t('x.users.save_error') };
            Object.keys(response[0].error.response.data).forEach(key => {
              let error = response[0].error.response.data[key][0];
              if (response[0].error.response.data[key][0] === 'The e-mail is invalid.') {
                error = 'invalid_email';
              }
              errors[key] = error;
            });
            return errors;
          }
          res();
        })
        .catch(error => {
          logger.info('Error on adding User', {
            error,
            info: {
              componentStack: 'UserList.tsx',
            },
          });
          res(error);
        });
    });
  };

  render() {
    const { loadingUsers, users, t } = this.props;
    return (
      <ItemsAmountRow>
        {loadingUsers ? (
          <LoadingSpinner />
        ) : (
          <MainContainerAnimation>
            {users && users.length > 0 ? (
              users.map(user => (
                <UserListItem
                  onSubmit={this.updateFullUser}
                  user={user}
                  key={user.uuid}
                  isOpen={this.props.openItem === user.uuid}
                  changeOpenItem={this.changeOpenItem}
                  onDelete={this.onDeleteUser}
                  onDeactivationToggle={this.onToggleUserDeactivatedStatus}
                />
              ))
            ) : (
              <NoUserFound data-translation="x.users.list.no_users.text">
                {t('x.users.list.no_users.text')}
              </NoUserFound>
            )}
          </MainContainerAnimation>
        )}
      </ItemsAmountRow>
    );
  }
}

const mapStateToProps = (state: StoreI): StateProps => ({
  users: getUsers(state),
  loadingUsers: state.userManagement.loading,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchUsers: params => dispatch(fetchUsers(params)),
  deleteUser: user => dispatch(deleteUser(user)),
  updateUser: (userId, values, fullName) => dispatch(updateUser(userId, values, fullName)),
  changeUserActiveStatus: (userId, deactivated, fullName) =>
    dispatch(changeUserActiveStatus(userId, deactivated, fullName)),
  fetchAttributesList: () => dispatch(fetchAttributesList()),
});

const UserListC = connect(mapStateToProps, mapDispatchToProps)(UserList);

export default UserListC;
